import dayjs from "dayjs";
import { useShallow } from "zustand/react/shallow";

import useCalendarStore from "@/store/calendarStore";
import type { DailyReservation, DailyReservationData } from "@/types/reservation";
import { cn } from "@/utils/cn";

const HOURS = Array.from({ length: 15 }, (_, i) => i + 8); // 08:00 ~ 22:00

const statusColor: Record<string, string> = {
  approve: "bg-green-100 border-green-300 text-green-700",
  request: "bg-amber-50 border-amber-200 text-amber-700",
  reject: "bg-red-50 border-red-200 text-red-600",
  cancel: "bg-gray-50 border-gray-200 text-gray-400",
};

interface DailyViewProps {
  selectedReservation?: DailyReservationData | null;
  onSelect?: (reservation: DailyReservationData | null) => void;
}

const DailyView = ({ selectedReservation, onSelect }: DailyViewProps) => {
  const [date, monthlyReservations] = useCalendarStore(
    useShallow(state => [state.date, state.monthlyReservations]),
  );

  // Get reservations for the selected date from monthly store data
  const dailyMatch = monthlyReservations?.find(
    (r: DailyReservation) =>
      dayjs(r.day).isSame(date, "day"),
  );
  const reservations: DailyReservationData[] = dailyMatch?.data ?? [];

  const getReservationStyle = (startTime: string, endTime: string) => {
    const startHour = parseInt(startTime.slice(0, 2), 10);
    const startMin = parseInt(startTime.slice(2, 4), 10);
    const endHour = parseInt(endTime.slice(0, 2), 10);
    const endMin = parseInt(endTime.slice(2, 4), 10);

    const top = ((startHour - 8) * 60 + startMin) * (3 / 60);
    const height = ((endHour - startHour) * 60 + (endMin - startMin)) * (3 / 60);

    return {
      top: `${top}rem`,
      height: `${Math.max(height, 1.5)}rem`,
    };
  };

  const formatTime = (t: string) => {
    if (!t || t.length < 4) return "";
    return `${t.slice(0, 2)}:${t.slice(2, 4)}`;
  };

  const handleClick = (e: React.MouseEvent, r: DailyReservationData) => {
    e.stopPropagation();
    if (selectedReservation?.rsvtId === r.rsvtId) {
      onSelect?.(null);
    } else {
      onSelect?.(r);
    }
  };

  return (
    <div className="relative flex overflow-x-auto rounded-lg border border-gray-200">
      {/* Time column */}
      <div className="shrink-0 border-r border-gray-100 bg-gray-50">
        {HOURS.map(hour => (
          <div
            key={hour}
            className="flex h-12 w-16 items-start justify-end border-b border-gray-100 pr-2 pt-0.5 text-[11px] text-gray-400"
          >
            {String(hour).padStart(2, "0")}:00
          </div>
        ))}
      </div>

      {/* Reservation area */}
      <div className="relative min-h-[45rem] flex-1" onClick={() => onSelect?.(null)}>
        {HOURS.map(hour => (
          <div key={hour} className="h-12 border-b border-gray-100" />
        ))}

        {reservations.map((r) => {
          const style = getReservationStyle(r.startTime, r.endTime);
          const colorCls = statusColor[r.sttCd] || statusColor.approve;
          const isSelected = selectedReservation?.rsvtId === r.rsvtId;

          return (
            <div
              key={r.rsvtId}
              onClick={(e) => handleClick(e, r)}
              className={cn(
                "absolute left-1 right-2 cursor-pointer rounded-md border px-2 py-1 text-xs transition-all",
                colorCls,
                isSelected
                  ? "z-20 -translate-y-0.5 ring-2 ring-primary shadow-lg scale-[1.02]"
                  : "hover:-translate-y-px hover:shadow-md",
              )}
              style={style}
            >
              <div className="flex items-center gap-2 font-medium">
                <span>{formatTime(r.startTime)} ~ {formatTime(r.endTime)}</span>
                <span className="truncate">{r.plcNm}</span>
              </div>
              <div className="truncate text-[10px] opacity-70">
                {r.useCnts}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DailyView;
